// app/hooks/useTimeLogs.ts
import { useState, useEffect, useRef, useCallback } from 'react';
import { AppState, Platform } from 'react-native';
import { useTranslation } from 'react-i18next';
import {
  startSession,
  startDriving,
  stopDriving,
  endSession,
  getDailyDrivingTime,
  getCurrentDriverState,  // Novo import
  getDriverStatus,
  ApiResponse,
  ErrorResponse,
  TimeLogEntry,
  DrivingLimitStatus,
  DrivingWeekLimits,
} from '../api/apiService';
import { SubmitHandler, UseFormReturn } from 'react-hook-form';
import { OdometerFormData } from '../lib/types';
import { getLocationString, isLocationUnavailableError, LOCATION_ERROR } from '../utils/locationUtils';
import { track, flush as flushAnalytics } from '../lib/analytics';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { syncDrivingLimitNotifications } from '../lib/drivingLimitNotifications';
import { storeDrivingLimitSnapshot } from '../lib/drivingLimitStatusNotification';
import { syncDrivingWeekLimitNotifications } from '../lib/drivingWeekLimitNotifications';

interface UseTimeLogsProps {
  selectedVehicle: number | null;
  form: UseFormReturn<OdometerFormData>;
  enabled?: boolean; // Only fetch driver data when enabled (for driver profile)
  onWorkCompanyRequired?: () => void;
}

export type DrivingActionResult =
  | boolean
  | 'location_permission_denied'
  | 'location_settings_invalid'
  | 'app_inactive';

const PENDING_STOP_DRIVING_KEY = 'pending_stop_driving';
const STOP_DRIVING_QUEUED_MESSAGE = 'Sessão parada guardada. Será enviada quando a ligação for restaurada.';
const START_TRACKING_INACTIVE_MESSAGE_KEY = 'alerts.reopenAppBeforeStartingTracking';

// Longer than the worst legitimate action: a 10s location fix plus start/stop
// driving's retry budget (~48s). Past this, an action still holding the UI is
// stranded, not slow, so recovering it cannot interrupt real work.
const STUCK_ACTION_THRESHOLD_MS = 60000;

// Tracking/session start must happen with the app foregrounded so the user can
// explicitly confirm the action and location permission flow can complete.
const canStartTimeLogSession = () => AppState.currentState === 'active';

// Pull the first human-readable error from a DRF field-error response so the UI
// can show the real backend message (e.g. `vehicle` → "Veículo não autorizado
// para este contexto.") instead of a generic fallback. Field order is not
// guaranteed, so we normalise array/string/JSON-string shapes consistently.
export const extractFirstFieldError = (
  errorData: unknown
): { field: string; message: string } | null => {
  if (!errorData || typeof errorData !== 'object') return null;
  for (const [field, value] of Object.entries(errorData as Record<string, unknown>)) {
    if (value === null || value === undefined) continue;
    let message = '';
    if (Array.isArray(value)) {
      message = String(value[0] ?? '');
    } else if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
        try {
          const parsed = JSON.parse(trimmed.replace(/'/g, '"'));
          message = Array.isArray(parsed) ? String(parsed[0] ?? '') : String(parsed);
        } catch {
          message = trimmed;
        }
      } else {
        message = trimmed;
      }
    } else if (typeof value === 'object') {
      // Nested non-field object (e.g. a nested serializer error) — recurse once.
      const nested = extractFirstFieldError(value);
      if (nested) return nested;
      continue;
    }
    if (message) return { field, message };
  }
  return null;
};

export const hasAlreadyStoppedDrivingMessage = (data: unknown): boolean => {
  if (!data || typeof data !== 'object') return false;

  const values = Object.values(data as Record<string, unknown>)
    .flatMap((value) => (Array.isArray(value) ? value : [value]))
    .filter((value): value is string => typeof value === 'string')
    .join(' ')
    .toLowerCase();

  return (
    values.includes('not currently driving') ||
    values.includes('already stopped') ||
    values.includes('já não está a conduzir') ||
    values.includes('nao esta a conduzir') ||
    values.includes('não está a conduzir')
  );
};

export const hasAlreadyStartedDrivingMessage = (data: unknown): boolean => {
  if (!data || typeof data !== 'object') return false;

  const values = Object.values(data as Record<string, unknown>)
    .flatMap((value) => (Array.isArray(value) ? value : [value]))
    .filter((value): value is string => typeof value === 'string')
    .join(' ')
    .toLowerCase();

  return (
    values.includes('dois eventos consecutivos de início de condução') ||
    values.includes('dois eventos consecutivos de inicio de conducao') ||
    (values.includes('drive_in') && values.includes('drive_out'))
  );
};

export const useTimeLogs = ({ selectedVehicle, form, enabled = true, onWorkCompanyRequired }: UseTimeLogsProps) => {
  const { t, i18n } = useTranslation();
  const [isSessionActive, setIsSessionActive] = useState(false);
  const [isDriving, setIsDriving] = useState(false);
  const [drivingTimeToday, setDrivingTimeToday] = useState('00:00');
  const [drivingLimit, setDrivingLimit] = useState<DrivingLimitStatus | null>(null);
  const [drivingWeekLimits, setDrivingWeekLimits] = useState<DrivingWeekLimits | null>(null);
  const [stateSince, setStateSince] = useState<string | null>(null);
  const [actionInProgress, setActionInProgress] = useState(false);
  const [isStartingDriving, setIsStartingDriving] = useState(false);
  const [isStoppingDriving, setIsStoppingDriving] = useState(false);

  const drivingTimeRequestInFlight = useRef(false);
  const driverStateRequestInFlight = useRef(false);
  const driverStatusRequestInFlight = useRef(false);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isMountedRef = useRef(true);
  const actionLockRef = useRef(false);
  // Wall-clock stamp of the action currently holding the UI, so the foreground
  // recovery below can tell a genuinely in-flight request from a stranded one.
  const actionStartedAtRef = useRef<number | null>(null);

  const createAttemptId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const getErrorReason = (error: unknown): string => {
    if (error instanceof Error) return error.message;
    return String(error ?? 'unknown_error');
  };

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  const fetchDriverState = useCallback(async () => {
    if (driverStateRequestInFlight.current) {
      return;
    }

    driverStateRequestInFlight.current = true;
    try {
      const response = await getCurrentDriverState();
      if (response.ok) {
        if (!isMountedRef.current) return;
        setIsSessionActive(response.data.is_session_active);
        setIsDriving(response.data.is_driving);
      } else if (response.status === 401) {
        // 401 is expected when user is not authenticated (logout, session expired)
        // Silently ignore to avoid noise in console
      }
    } catch (error) {
      // Only log actual network/exceptions errors, not HTTP errors (which are in response.status)
      console.error('Error fetching driver state:', error);
    } finally {
      driverStateRequestInFlight.current = false;
    }
  }, []);

  const fetchDrivingTime = useCallback(async () => {
    if (drivingTimeRequestInFlight.current) {
      return;
    }

    drivingTimeRequestInFlight.current = true;
    try {
      const response = await getDailyDrivingTime();
      if (response.ok) {
        if (!isMountedRef.current) return;
        setDrivingTimeToday(response.data.driving_time_today);
      } else if (response.status === 401) {
        // 401 is expected when user is not authenticated (logout, session expired)
        // Silently ignore to avoid noise in console
      }
    } catch (error) {
      // Only log actual network/exceptions errors, not HTTP errors (which are in response.status)
      console.error('Error fetching driving time:', error);
    } finally {
      drivingTimeRequestInFlight.current = false;
    }
  }, []);

  const fetchDriverStatus = useCallback(async () => {
    if (driverStatusRequestInFlight.current) {
      return;
    }

    driverStatusRequestInFlight.current = true;
    try {
      const response = await getDriverStatus();
      if (response.ok) {
        if (!isMountedRef.current) return;
        setIsSessionActive(response.data.is_session_active);
        setIsDriving(response.data.is_driving);
        setDrivingTimeToday(response.data.driving_time_today);
        setDrivingLimit(response.data.driving_limit ?? null);
        setDrivingWeekLimits(response.data.driving_week_limits ?? null);
        setStateSince(response.data.state_since ?? null);
      } else if (response.status === 401) {
        // 401 is expected when user is not authenticated (logout, session expired)
        // Silently ignore to avoid noise in console
      } else {
        // Fallback to legacy endpoints if needed
        await Promise.all([fetchDrivingTime(), fetchDriverState()]);
      }
    } catch (error) {
      // Only log actual network/exceptions errors, not HTTP errors (which are in response.status)
      console.error('Error fetching driver status:', error);
    } finally {
      driverStatusRequestInFlight.current = false;
    }
  }, [fetchDrivingTime, fetchDriverState]);

  const handleStartSession: SubmitHandler<OdometerFormData> = async (data) => {
    if (actionInProgress) return false;
    const attemptId = createAttemptId();
    
    if (!selectedVehicle) {
      form.setError('odometer', {
        type: 'manual',
        message: t('selectVehicle')
      });
      return false;
    }
    if (data.odometer === undefined || data.odometer === null || Number.isNaN(data.odometer) || data.odometer <= 0) {
      form.setError('odometer', {
        type: 'manual',
        message: t('odometerRequired')
      });
      return false;
    }
    
    if (!canStartTimeLogSession()) {
      form.setError('odometer', {
        type: 'manual',
        message: t(START_TRACKING_INACTIVE_MESSAGE_KEY, 'Reopen the app before starting driving or session tracking.'),
      });
      return 'app_inactive';
    }

    setActionInProgress(true);
    
    try {
      track('driver_session_start_attempt', {
        vehicle_id: selectedVehicle,
        attempt_id: attemptId,
        platform: Platform.OS,
      });

      const location = await getLocationString();
      const response = await startSession(selectedVehicle, data.odometer, location);
      if (response.ok) {
        // Update state directly from the response
        const sessionData = response.data as any;
        setIsSessionActive(sessionData.current_state.is_session_active);
        setIsDriving(sessionData.current_state.is_driving);
        setDrivingTimeToday(sessionData.driving_time_today);
        setDrivingLimit(sessionData.driving_limit ?? null);
        setDrivingWeekLimits(sessionData.driving_week_limits ?? null);
        setStateSince(sessionData.current_state.state_since ?? null);

        track('driver_session_started', {
          vehicle_id: selectedVehicle,
          driving_time_today: sessionData.driving_time_today,
        });
        flushAnalytics();

        // Check for velocity warning
        if (sessionData.odometer_velocity_warning && sessionData.odometer_velocity_warning.exceeds_limit) {
          track('driver_session_start_velocity_warning', {
            vehicle_id: selectedVehicle,
          });
          return {
            status: 'velocity_warning',
            warning: sessionData.odometer_velocity_warning
          };
        }
        
        return true;
      } else {
        const errorData = response.data as unknown as ErrorResponse;
        if (onWorkCompanyRequired && ['work_company_required', 'work_identity_unresolved'].includes(errorData.code)) {
          onWorkCompanyRequired();
          return false;
        }
        if ((errorData as any).odometer_velocity_warning?.exceeds_limit) {
          track('driver_session_start_velocity_warning', {
            vehicle_id: selectedVehicle,
          });
          return {
            status: 'velocity_warning',
            warning: (errorData as any).odometer_velocity_warning
          };
        }
        
        // Check for odometer decrease error
        if (errorData.odometer_decrease_detected && errorData.previous_session_out) {
          track('driver_session_start_failed', {
            vehicle_id: selectedVehicle,
            reason: 'odometer_decrease',
            error_reason: 'odometer_decrease',
            attempt_id: attemptId,
            platform: Platform.OS,
          });
          return {
            status: 'odometer_decrease',
            previousSessionOut: errorData.previous_session_out,
            newOdometer: data.odometer
          };
        }
        
        if (errorData.odometer) {
          let message = '';
          if (Array.isArray(errorData.odometer)) {
            message = errorData.odometer[0];
          } else if (typeof errorData.odometer === 'string') {
            try {
              let parsed: any = errorData.odometer;
              if (typeof parsed === 'string') {
                parsed = parsed.replace(/'/g, '"');
                parsed = JSON.parse(parsed);
              }
              if (Array.isArray(parsed)) {
                message = parsed[0];
              } else {
                message = errorData.odometer;
              }
            } catch {
              message = errorData.odometer;
            }
          }
          track('driver_session_start_failed', {
            vehicle_id: selectedVehicle,
            reason: 'odometer_validation',
            error_reason: 'odometer_validation',
            attempt_id: attemptId,
            platform: Platform.OS,
          });
          form.setError('odometer', {
            type: 'manual',
            message,
          });
        } else {
          // Surface the real backend error instead of the generic message.
          // The start-session endpoint can reject with a field error such as
          // `vehicle` ("Veículo não autorizado para este contexto.") — that
          // must be shown verbatim so the user (and E2E) sees the real cause.
          const firstFieldError = extractFirstFieldError(errorData);
          track('driver_session_start_failed', {
            vehicle_id: selectedVehicle,
            reason: firstFieldError ? 'field_error' : 'unknown',
            error_reason: firstFieldError?.field ?? 'api_error',
            status: response.status,
            attempt_id: attemptId,
            platform: Platform.OS,
          });
          form.setError('odometer', {
            type: 'manual',
            message: firstFieldError?.message || t('errorStartingSession'),
          });
        }
        return false;
      }
    } catch (error: any) {
      const isLocationUnavailable = isLocationUnavailableError(error);
      if (isLocationUnavailable) {
        track('driver_session_start_failed', {
          vehicle_id: selectedVehicle,
          reason: error.message === LOCATION_ERROR.SETTINGS_INVALID ? 'location_settings_invalid' : 'location_permission_denied',
          error_reason: error.message,
          attempt_id: attemptId,
          platform: Platform.OS,
        });
        form.setError('odometer', {
          type: 'manual',
          message: error.message,
        });
        return 'location_denied';
      }
      track('driver_session_start_failed', {
        vehicle_id: selectedVehicle,
        reason: 'exception',
        error_reason: getErrorReason(error),
        attempt_id: attemptId,
        platform: Platform.OS,
      });
      console.error('Error starting session:', error);
      form.setError('odometer', {
        type: 'manual',
        message: t('errorStartingSession')
      });
      return false;
    } finally {
      setActionInProgress(false);
    }
  };

  // Allows starting a session when a valid WKT location is provided manually (e.g., via geocoding)
  const handleStartSessionWithProvidedLocation = async (data: OdometerFormData, locationWkt: string) => {
    if (actionInProgress) return false;
    const attemptId = createAttemptId();
    
    if (!selectedVehicle) {
      form.setError('odometer', {
        type: 'manual',
        message: t('selectVehicle')
      });
      return false;
    }
    if (data.odometer === undefined || data.odometer === null || Number.isNaN(data.odometer) || data.odometer <= 0) {
      form.setError('odometer', {
        type: 'manual',
        message: t('odometerRequired')
      });
      return false;
    }
    
    if (!canStartTimeLogSession()) {
      form.setError('odometer', {
        type: 'manual',
        message: t(START_TRACKING_INACTIVE_MESSAGE_KEY, 'Reopen the app before starting driving or session tracking.'),
      });
      return 'app_inactive';
    }

    setActionInProgress(true);
    
    try {
      track('driver_session_start_attempt', {
        vehicle_id: selectedVehicle,
        location_source: 'manual',
        attempt_id: attemptId,
        platform: Platform.OS,
      });

      const response = await startSession(selectedVehicle, data.odometer, locationWkt);
      if (response.ok) {
        // Update state directly from the response
        const sessionData = response.data as any;
        setIsSessionActive(sessionData.current_state.is_session_active);
        setIsDriving(sessionData.current_state.is_driving);
        setDrivingTimeToday(sessionData.driving_time_today);
        setDrivingLimit(sessionData.driving_limit ?? null);
        setDrivingWeekLimits(sessionData.driving_week_limits ?? null);
        setStateSince(sessionData.current_state.state_since ?? null);

        track('driver_session_started', {
          vehicle_id: selectedVehicle,
          driving_time_today: sessionData.driving_time_today,
          location_source: 'manual',
        });
        flushAnalytics();

        // Check for velocity warning
        if (sessionData.odometer_velocity_warning && sessionData.odometer_velocity_warning.exceeds_limit) {
          track('driver_session_start_velocity_warning', {
            vehicle_id: selectedVehicle,
            location_source: 'manual',
          });
          return {
            status: 'velocity_warning',
            warning: sessionData.odometer_velocity_warning
          };
        }
        
        return true;
      } else {
        const errorData = response.data as unknown as ErrorResponse;
        if (onWorkCompanyRequired && ['work_company_required', 'work_identity_unresolved'].includes(errorData.code)) {
          onWorkCompanyRequired();
          return false;
        }
        if ((errorData as any).odometer_velocity_warning?.exceeds_limit) {
          track('driver_session_start_velocity_warning', {
            vehicle_id: selectedVehicle,
            location_source: 'manual',
          });
          return {
            status: 'velocity_warning',
            warning: (errorData as any).odometer_velocity_warning
          };
        }
        
        // Check for odometer decrease error
        if (errorData.odometer_decrease_detected && errorData.previous_session_out) {
          track('driver_session_start_failed', {
            vehicle_id: selectedVehicle,
            location_source: 'manual',
            reason: 'odometer_decrease',
            error_reason: 'odometer_decrease',
            attempt_id: attemptId,
            platform: Platform.OS,
          });
          return {
            status: 'odometer_decrease',
            previousSessionOut: errorData.previous_session_out,
            newOdometer: data.odometer
          };
        }
        
        if (errorData.odometer) {
          let message = '';
          if (Array.isArray(errorData.odometer)) {
            message = errorData.odometer[0];
          } else if (typeof errorData.odometer === 'string') {
            try {
              let parsed: any = errorData.odometer;
              if (typeof parsed === 'string') {
                parsed = parsed.replace(/'/g, '"');
                parsed = JSON.parse(parsed);
              }
              if (Array.isArray(parsed)) {
                message = parsed[0];
              } else {
                message = errorData.odometer;
              }
            } catch {
              message = errorData.odometer;
            }
          }
          track('driver_session_start_failed', {
            vehicle_id: selectedVehicle,
            location_source: 'manual',
            reason: 'odometer_validation',
            error_reason: 'odometer_validation',
            attempt_id: attemptId,
            platform: Platform.OS,
          });
          form.setError('odometer', {
            type: 'manual',
            message,
          });
        } else {
          track('driver_session_start_failed', {
            vehicle_id: selectedVehicle,
            location_source: 'manual',
            reason: 'unknown',
            error_reason: 'api_error',
            status: response.status,
            attempt_id: attemptId,
            platform: Platform.OS,
          });
          form.setError('odometer', {
            type: 'manual',
            message: t('errorStartingSession')
          });
        }
        return false;
      }
    } catch (error) {
      track('driver_session_start_failed', {
        vehicle_id: selectedVehicle,
        location_source: 'manual',
        reason: 'exception',
        error_reason: getErrorReason(error),
        attempt_id: attemptId,
        platform: Platform.OS,
      });
      console.error('Error starting session with provided location:', error);
      form.setError('odometer', {
        type: 'manual',
        message: t('errorStartingSession')
      });
      return false;
    } finally {
      setActionInProgress(false);
    }
  };

  const handleStartDriving = async (): Promise<DrivingActionResult> => {
    if (actionInProgress || actionLockRef.current || !selectedVehicle) return false;
    actionLockRef.current = true;
    const attemptId = createAttemptId();

    if (!canStartTimeLogSession()) {
      actionLockRef.current = false;
      alert(t(START_TRACKING_INACTIVE_MESSAGE_KEY, 'Reopen the app before starting driving or session tracking.'));
      return 'app_inactive';
    }
    
    setActionInProgress(true);
    setIsStartingDriving(true);
    
    // Optimistic update
    setIsDriving(true);
    
    try {
      track('driver_start_driving_attempt', {
        vehicle_id: selectedVehicle,
        attempt_id: attemptId,
        platform: Platform.OS,
      });

      const location = await getLocationString();
      const response = await startDriving(selectedVehicle, location);
      if (response.ok) {
        // Confirm with server state
        const drivingData = response.data as any;
        setIsSessionActive(drivingData.current_state.is_session_active);
        setIsDriving(drivingData.current_state.is_driving);
        setDrivingTimeToday(drivingData.driving_time_today);
        setDrivingLimit(drivingData.driving_limit ?? null);
        setDrivingWeekLimits(drivingData.driving_week_limits ?? null);
        setStateSince(drivingData.current_state.state_since ?? null);

        track('driver_start_driving', {
          vehicle_id: selectedVehicle,
          driving_time_today: drivingData.driving_time_today,
        });
        flushAnalytics();
        return true;
      } else {
        const isRaceConditionStart =
          response.status === 400 && hasAlreadyStartedDrivingMessage(response.data);

        track('driver_start_driving_failed', {
          vehicle_id: selectedVehicle,
          reason: isRaceConditionStart ? 'already_started_race_condition' : 'api_error',
          error_reason: isRaceConditionStart ? 'already_started_race_condition' : 'api_error',
          status: response.status,
          attempt_id: attemptId,
          platform: Platform.OS,
        });

        if (isRaceConditionStart) {
          // Server already considers the driver as driving. Keep UI aligned and avoid noisy error alert.
          setIsDriving(true);
          // Best-effort sync with backend to ensure timers/state are correct
          void fetchDriverStatus();
          return true;
        }

        // Revert on error
        setIsDriving(false);
        alert(`${t('errorStartingDriving')}: ${JSON.stringify(response.data)}`);
        return false;
      }
    } catch (error: any) {
      // Revert on error
      setIsDriving(false);
      const isLocationUnavailable =
        error?.message === LOCATION_ERROR.PERMISSION_DENIED ||
        error?.message === LOCATION_ERROR.SETTINGS_INVALID;
      if (isLocationUnavailable) {
        const locationFailureReason =
          error.message === LOCATION_ERROR.SETTINGS_INVALID
            ? 'location_settings_invalid'
            : 'location_permission_denied';

        track('driver_start_driving_failed', {
          vehicle_id: selectedVehicle,
          reason: locationFailureReason,
          error_reason: locationFailureReason,
          attempt_id: attemptId,
          platform: Platform.OS,
        });
        return locationFailureReason;
      }
      track('driver_start_driving_failed', {
        vehicle_id: selectedVehicle,
        reason: 'exception',
        error_reason: getErrorReason(error),
        attempt_id: attemptId,
        platform: Platform.OS,
      });
      console.error('Error starting driving:', error);
      alert(t('errorStartingDriving'));
      return false;
    } finally {
      actionLockRef.current = false;
      setActionInProgress(false);
      setIsStartingDriving(false);
    }
  };

  const handleStopDriving = async (): Promise<DrivingActionResult> => {
    if (actionInProgress || actionLockRef.current || !selectedVehicle) return false;
    actionLockRef.current = true;
    const attemptId = createAttemptId();

    setActionInProgress(true);
    setIsStoppingDriving(true);

    // Optimistic update
    setIsDriving(false);

    let location = '';

    const persistPendingStopDriving = async (vehicle: number, pendingLocation: string) => {
      await AsyncStorage.setItem(
        PENDING_STOP_DRIVING_KEY,
        JSON.stringify({
          vehicle,
          location: pendingLocation,
          timestamp: Date.now(),
        })
      );
    };

    try {
      track('driver_stop_driving_attempt', {
        vehicle_id: selectedVehicle,
        attempt_id: attemptId,
        platform: Platform.OS,
      });

      location = await getLocationString();
      const response = await stopDriving(selectedVehicle, location);
      if (response.ok) {
        // Confirm with server state
        const drivingData = response.data as any;
        setIsSessionActive(drivingData.current_state.is_session_active);
        setIsDriving(drivingData.current_state.is_driving);
        setDrivingTimeToday(drivingData.driving_time_today);
        setDrivingLimit(drivingData.driving_limit ?? null);
        setDrivingWeekLimits(drivingData.driving_week_limits ?? null);
        setStateSince(drivingData.current_state.state_since ?? null);

        track('driver_stop_driving', {
          vehicle_id: selectedVehicle,
          driving_time_today: drivingData.driving_time_today,
        });
        flushAnalytics();
        return true;
      } else {
        const isRaceConditionStop =
          response.status === 400 && hasAlreadyStoppedDrivingMessage(response.data);
        const isNetworkFailure = response.errorType === 'NETWORK' || response.status === 0;

        track('driver_stop_driving_failed', {
          vehicle_id: selectedVehicle,
          reason: isRaceConditionStop
            ? 'already_stopped_race_condition'
            : isNetworkFailure
              ? 'network'
              : 'api_error',
          error_reason: isRaceConditionStop
            ? 'already_stopped_race_condition'
            : isNetworkFailure
              ? 'network'
              : 'api_error',
          status: response.status,
          attempt_id: attemptId,
          platform: Platform.OS,
        });

        if (isRaceConditionStop) {
          // Server already considers the driver stopped. Keep UI aligned and avoid noisy error alert.
          setIsDriving(false);
          void fetchDriverStatus();
          return true;
        }

        if (isNetworkFailure) {
          try {
            await persistPendingStopDriving(selectedVehicle, location);
            alert(STOP_DRIVING_QUEUED_MESSAGE);
            return true;
          } catch (storageError) {
            console.warn('Failed to persist pending stop-driving request', storageError);
          }
        }

        // Revert on error
        setIsDriving(true);
        alert(`${t('errorStoppingDriving')}: ${JSON.stringify(response.data)}`);
        return false;
      }
    } catch (error: any) {
      // Revert on error
      setIsDriving(true);
      const isLocationUnavailable =
        error?.message === LOCATION_ERROR.PERMISSION_DENIED ||
        error?.message === LOCATION_ERROR.SETTINGS_INVALID;
      if (isLocationUnavailable) {
        const locationFailureReason =
          error.message === LOCATION_ERROR.SETTINGS_INVALID
            ? 'location_settings_invalid'
            : 'location_permission_denied';

        track('driver_stop_driving_failed', {
          vehicle_id: selectedVehicle,
          reason: locationFailureReason,
          error_reason: locationFailureReason,
          attempt_id: attemptId,
          platform: Platform.OS,
        });
        return locationFailureReason;
      }

      const errorReason = getErrorReason(error).toLowerCase();
      const isNetworkFailure =
        error?.errorType === 'NETWORK' ||
        errorReason.includes('network') ||
        errorReason.includes('fetch');

      track('driver_stop_driving_failed', {
        vehicle_id: selectedVehicle,
        reason: isNetworkFailure ? 'network_exception' : 'exception',
        error_reason: getErrorReason(error),
        attempt_id: attemptId,
        platform: Platform.OS,
      });
      console.error('Error stopping driving:', error);

      if (isNetworkFailure) {
        try {
          await persistPendingStopDriving(selectedVehicle, location);
          setIsDriving(false);
          alert(STOP_DRIVING_QUEUED_MESSAGE);
          return true;
        } catch (storageError) {
          console.warn('Failed to persist pending stop-driving request', storageError);
        }
      }

      alert(t('errorStoppingDriving'));
      return false;
    } finally {
      actionLockRef.current = false;
      setActionInProgress(false);
      setIsStoppingDriving(false);
    }
  };

  // Initial fetch on mount only - only if enabled (driver profile)
  useEffect(() => {
    if (!enabled) {
      return;
    }

    void fetchDriverStatus();
  }, [enabled, fetchDriverStatus]); // Run on mount and keep stable refs

  useEffect(() => {
    if (!enabled) {
      return;
    }

    let cancelled = false;

    const replayPendingStopDriving = async () => {
      try {
        const rawPending = await AsyncStorage.getItem(PENDING_STOP_DRIVING_KEY);
        if (!rawPending) {
          return;
        }

        const pending = JSON.parse(rawPending) as {
          vehicle?: number;
          location?: string;
          timestamp?: number;
        };

        if (!pending.vehicle) {
          await AsyncStorage.removeItem(PENDING_STOP_DRIVING_KEY);
          return;
        }

        const replayLocation =
          pending.location || (await getLocationString().catch(() => ''));

        if (!replayLocation) {
          return;
        }

        const response = await stopDriving(pending.vehicle, replayLocation);
        const isRaceConditionStop =
          response.status === 400 && hasAlreadyStoppedDrivingMessage(response.data);

        if (response.ok || isRaceConditionStop) {
          await AsyncStorage.removeItem(PENDING_STOP_DRIVING_KEY);
          if (!cancelled) {
            setIsDriving(false);
            void fetchDriverStatus();
          }

          track('driver_stop_driving_replayed', {
            vehicle_id: pending.vehicle,
            platform: Platform.OS,
            had_race_condition: isRaceConditionStop,
          });
        }
      } catch (error) {
        console.warn('Failed to replay pending stop-driving request', error);
      }
    };

    void replayPendingStopDriving();

    return () => {
      cancelled = true;
    };
  }, [enabled, fetchDriverStatus]);

  // Stamped from the state itself rather than from each handler, so any action
  // that holds the UI is covered, including ones added later.
  useEffect(() => {
    actionStartedAtRef.current = actionInProgress ? Date.now() : null;
  }, [actionInProgress]);

  // A request that never settles leaves the `finally` in the handlers above
  // unreached, so actionInProgress and isStartingDriving stay true, every
  // control stays disabled, and the driver has to force-quit the app (#316).
  // apiFetch now bounds each call, but a suspended JS thread can outlive that
  // timer — Android freezes timers while the app is backgrounded, which is
  // exactly what happens when a driver locks the screen mid-action. Coming back
  // to the foreground is the last safety net.
  useEffect(() => {
    const subscription = AppState.addEventListener('change', (nextState) => {
      if (nextState !== 'active') {
        return;
      }

      const startedAt = actionStartedAtRef.current;
      if (startedAt === null || Date.now() - startedAt < STUCK_ACTION_THRESHOLD_MS) {
        return;
      }

      actionStartedAtRef.current = null;
      actionLockRef.current = false;
      setActionInProgress(false);
      setIsStartingDriving(false);
      setIsStoppingDriving(false);

      // The optimistic isDriving set at action start may now disagree with the
      // server, so take the server's word rather than guessing.
      void fetchDriverStatus();
    });

    return () => subscription?.remove();
  }, [fetchDriverStatus]);

  // Polling effect - separate from initial fetch - only if enabled
  useEffect(() => {
    if (!enabled || !isSessionActive) {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      return;
    }

    if (pollIntervalRef.current) {
      return;
    }

    pollIntervalRef.current = setInterval(async () => {
      if (actionInProgress) return;
      if (driverStatusRequestInFlight.current || drivingTimeRequestInFlight.current || driverStateRequestInFlight.current) {
        return;
      }
      await fetchDriverStatus();
    }, 30000);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [enabled, isSessionActive, fetchDriverStatus]);
  // Rolling 24h limit (Lei 45/2018): keep the scheduled warnings in line with
  // the latest server projection; cancelled as soon as driving stops.
  const translateRef = useRef(t);
  translateRef.current = t;
  const languageRef = useRef(i18n?.language);
  languageRef.current = i18n?.language;
  useEffect(() => {
    // Leaving the driver profile cancels what was already handed to the OS.
    void syncDrivingLimitNotifications(enabled ? drivingLimit : null, isDriving, translateRef.current, languageRef.current);
    // Read by the background location task for the "Driving" notification countdown.
    void storeDrivingLimitSnapshot(enabled && isDriving ? drivingLimit : null);
  }, [enabled, drivingLimit, isDriving]);
  // Week and 17-week average (ALL-136): 80 % and 95 %, once per threshold per week.
  useEffect(() => {
    void syncDrivingWeekLimitNotifications(
      enabled ? drivingWeekLimits : null,
      isDriving,
      translateRef.current,
      languageRef.current,
    );
  }, [enabled, drivingWeekLimits, isDriving]);
  useEffect(
    () => () => {
      void syncDrivingLimitNotifications(null, false, translateRef.current);
      void storeDrivingLimitSnapshot(null);
      void syncDrivingWeekLimitNotifications(null, false, translateRef.current);
    },
    [],
  );

  return {
    isSessionActive,
    isDriving,
    drivingTimeToday,
    drivingLimit,
    drivingWeekLimits,
    stateSince,
    actionInProgress,
    isStartingDriving,
    isStoppingDriving,
    fetchDrivingTime,
    handleStartSession,
    handleStartSessionWithProvidedLocation,
    handleStartDriving,
    handleStopDriving,
    fetchDriverState,
    fetchDriverStatus,
  };
};
